import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { openExternal } from '../shared/lib/nav';

// FF&E / Housewares inventory (Owner-Ops round #8). Durable goods per property
// — pots/pans/bakeware/dishware/etc., DISTINCT from consumables (Supplies).
// Tracks condition + a reference photo, and runs an AI "deep kitchen" inspection
// that flags WORN or MISSING items from photos. Backed by /api/assetops/ffe.
const API = '/api/assetops';
const COND_STYLE: Record<string, string> = {
  good: 'background:#dcfce7;color:#166534;', worn: 'background:#fef9c3;color:#854d0e;',
  missing: 'background:#fee2e2;color:#991b1b;', unknown: 'background:#f1f5f9;color:#64748b;',
};
const CONDS = ['good', 'worn', 'missing', 'unknown'];

export class FfePage {
  private container: HTMLElement;
  private board: any = null;
  private listings: { id: string; name: string }[] = [];
  private selectedId = '';
  private data: any = null;
  private busy = false;
  private note = '';
  private lastFindings: any[] | null = null;
  // AI-unblock: the deep inspection saves the photos and answers right away;
  // the AI findings land later. Id of the deep inspection we're waiting on.
  private pendingDi = '';
  private keptByHand: string[] = [];
  // AI-unblock (review #16): items a NEWER overlapping deep inspection had already graded — not "set by hand".
  private keptNewer: string[] = [];

  constructor(container: HTMLElement) { this.container = container; }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="ffe-body"><div style="padding:40px;text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    const [board, listings] = await Promise.all([
      fetch(`${API}/ffe/board`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/listings/active').then(r => r.ok ? r.json() : []).catch(() => []),
    ]);
    this.board = board;
    this.listings = (Array.isArray(listings) ? listings : []).map((l: any) => ({ id: String(l.id), name: l.short_name || l.name || String(l.id) }));
    this.draw();
  }

  private async loadProperty(): Promise<void> {
    if (!this.selectedId) { this.data = null; this.draw(); return; }
    this.data = await fetch(`${API}/ffe/${encodeURIComponent(this.selectedId)}`).then(r => r.ok ? r.json() : null).catch(() => null);
    // AI-unblock: a deep inspection still being analyzed (from this tap, another
    // device, or before a reload) — keep checking until its findings land.
    const di0 = this.data && (this.data.deep_inspections || [])[0];
    if (di0 && di0.ai_status === 'pending' && this.pendingDi !== String(di0.id)) this.watchDeepInspection(this.selectedId, String(di0.id));
    this.draw();
  }

  private async reloadBoard(): Promise<void> {
    this.board = await fetch(`${API}/ffe/board`).then(r => r.ok ? r.json() : this.board).catch(() => this.board);
  }

  private draw(): void {
    const body = document.getElementById('ffe-body');
    if (!body) return;
    if (!this.board) { body.innerHTML = emptyState('FF&E board unavailable', 'GET /api/assetops/ffe/board failed.', '⚠️'); return; }
    const t = this.board.totals || { tracked: 0, worn: 0, missing: 0 };

    const propOpts = ['<option value="">— select a property —</option>', ...this.listings.map(l =>
      `<option value="${this.esc(l.id)}" ${this.selectedId === l.id ? 'selected' : ''}>${this.esc(l.name)}</option>`)].join('');

    body.innerHTML = `
      <style>
        .ffe-head { display:flex; align-items:baseline; gap:12px; margin-bottom:14px; flex-wrap:wrap; }
        .ffe-head h2 { margin:0; font-size:18px; font-weight:600; } .ffe-head .sub { font-size:12px; color:var(--text-muted); }
        .ffe-kpis { display:flex; gap:10px; margin-bottom:16px; flex-wrap:wrap; }
        .ffe-kpi { min-width:120px; padding:10px 14px; border:1px solid var(--border); border-radius:var(--radius); background:#fff; }
        .ffe-kpi-v { font-size:22px; font-weight:600; } .ffe-kpi-l { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .ffe-sel { padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff; min-width:260px; }
        .ffe-card { background:#fff; border:1px solid var(--border); border-radius:12px; margin-top:14px; overflow:hidden; }
        .ffe-cat { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); padding:10px 16px 4px; }
        .ffe-row { display:flex; align-items:center; gap:12px; padding:9px 16px; border-top:1px solid var(--border-soft,#f1f5f9); }
        .ffe-name { flex:1; font-size:13px; font-weight:500; } .ffe-qty { font-size:11px; color:var(--text-faint); }
        .ffe-badge { font-size:10px; font-weight:700; padding:2px 9px; border-radius:999px; text-transform:capitalize; }
        .ffe-cond { padding:5px 8px; border:1px solid var(--border); border-radius:7px; font-size:12px; background:#fff; }
        .ffe-thumb { width:34px; height:34px; border-radius:6px; object-fit:cover; border:1px solid var(--border); cursor:pointer; }
        .ffe-photo-btn { font-size:11px; color:var(--text-muted); border:1px dashed var(--border); border-radius:6px; padding:4px 8px; cursor:pointer; }
        .ffe-btn { font-size:12px; font-weight:600; padding:8px 15px; border:1px solid var(--border); border-radius:8px; background:#fff; cursor:pointer; }
        .ffe-btn.primary { background:var(--accent); color:#fff; border-color:var(--accent); }
        .ffe-btn.deep { background:#1e1b4b; color:#fff; border-color:#1e1b4b; }
        .ffe-note { font-size:12px; color:var(--text-muted); }
        .ffe-find { background:var(--bg-subtle); border:1px solid var(--border); border-radius:10px; padding:12px 14px; margin-top:12px; }
        .ffe-find h4 { margin:0 0 6px; font-size:13px; } .ffe-find .fr { font-size:12px; padding:3px 0; }
        .ffe-mini { font-size:12px; line-height:1; padding:5px 8px; border:1px solid var(--border); border-radius:6px; background:#fff; cursor:pointer; color:var(--text-muted); }
        .ffe-mini:hover { border-color:#9ca3af; color:var(--text); }
        .ffe-mini.danger:hover { border-color:#fca5a5; color:#b91c1c; }
        .ffe-add { display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:12px 16px; border-top:1px solid var(--border); background:var(--bg-subtle); }
        .ffe-in { padding:7px 10px; border:1px solid var(--border); border-radius:7px; font-size:12.5px; background:#fff; }
        #ffe-add-name { flex:1; min-width:160px; }
      </style>
      <div class="ffe-head">
        <h2>FF&amp;E &amp; Housewares</h2>
        <span class="sub">durable goods per property — condition tracked, AI deep-kitchen inspection flags worn &amp; missing</span>
      </div>
      <div class="ffe-kpis">
        <div class="ffe-kpi"><div class="ffe-kpi-v">${this.board.listings}</div><div class="ffe-kpi-l">Properties</div></div>
        <div class="ffe-kpi"><div class="ffe-kpi-v">${t.tracked}</div><div class="ffe-kpi-l">Items tracked</div></div>
        <div class="ffe-kpi" style="${t.worn ? COND_STYLE.worn : ''}"><div class="ffe-kpi-v">${t.worn}</div><div class="ffe-kpi-l">Worn</div></div>
        <div class="ffe-kpi" style="${t.missing ? COND_STYLE.missing : ''}"><div class="ffe-kpi-v">${t.missing}</div><div class="ffe-kpi-l">Missing</div></div>
      </div>
      <select class="ffe-sel" id="ffe-prop">${propOpts}</select>
      ${this.note ? `<span class="ffe-note" style="margin-left:12px;">${this.esc(this.note)}</span>` : ''}
      <div id="ffe-detail">${this.selectedId ? this.renderDetail() : '<div style="padding:30px; text-align:center; color:var(--text-muted); font-size:13px;">Pick a property to see its housewares inventory.</div>'}</div>`;
    this.bind();
  }

  private renderDetail(): string {
    if (!this.data) return `<div style="padding:24px; text-align:center;">${LoadingSpinner.render()}</div>`;
    const items: any[] = this.data.items || [];
    const cats = [...new Set(items.map(i => i.category))];
    const groups = cats.map(cat => {
      const rows = items.filter(i => i.category === cat).map(i => `
        <div class="ffe-row" data-item="${this.esc(i.id)}">
          <span class="ffe-badge" style="${COND_STYLE[i.condition] || COND_STYLE.unknown}">${this.esc(i.condition)}</span>
          <span class="ffe-name">${this.esc(i.name)} <span class="ffe-qty">· expect ${i.expected_qty}</span>${i.notes ? `<div style="font-size:10.5px; color:var(--text-faint);">${this.esc(i.notes)}</div>` : ''}</span>
          ${i.photo ? `<img class="ffe-thumb" src="${this.esc(i.photo)}" data-photo="${this.esc(i.photo)}">` : `<label class="ffe-photo-btn">📷<input type="file" accept="image/*" capture="environment" data-photo-item="${this.esc(i.id)}" style="display:none;"></label>`}
          <select class="ffe-cond" data-cond-item="${this.esc(i.id)}">${CONDS.map(c => `<option value="${c}" ${i.condition === c ? 'selected' : ''}>${c}</option>`).join('')}</select>
          <button class="ffe-mini" data-edit-item="${this.esc(i.id)}" title="Rename / change expected count">✎</button>
          <button class="ffe-mini danger" data-del-item="${this.esc(i.id)}" title="Remove this item from the kit">✕</button>
        </div>`).join('');
      return `<div class="ffe-cat">${this.esc(cat)}</div>${rows}`;
    }).join('');
    const catOpts = [...new Set([...cats, 'Cookware', 'Bakeware', 'Cutlery', 'Utensils', 'Dishware', 'Glassware', 'Appliances', 'Linens', 'General'])]
      .map(c => `<option value="${this.esc(c)}">${this.esc(c)}</option>`).join('');
    const di = (this.data.deep_inspections || [])[0];
    return `
      <div class="ffe-card">
        <div style="display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid var(--border);">
          <strong style="font-size:14px;">${this.esc(this.data.listing_name)} · ${items.length} items</strong>
          <label class="ffe-btn deep">${this.busy ? 'Uploading photos…' : '🔍 Deep kitchen inspection'}<input type="file" accept="image/*" capture="environment" multiple id="ffe-deep" style="display:none;" ${this.busy ? 'disabled' : ''}></label>
        </div>
        ${groups}
        <div class="ffe-add">
          <input class="ffe-in" id="ffe-add-name" placeholder="add an item — e.g. Dutch oven">
          <select class="ffe-in" id="ffe-add-cat">${catOpts}</select>
          <input class="ffe-in" id="ffe-add-qty" type="number" min="1" value="1" style="width:64px;" title="expected count">
          <button class="ffe-btn primary" id="ffe-add-btn">+ Add item</button>
        </div>
      </div>
      ${this.lastFindings ? `
        <div class="ffe-find">
          <h4>AI deep-inspection findings</h4>
          ${this.lastFindings.length ? this.lastFindings.map((f: any) => `<div class="fr"><span class="ffe-badge" style="${COND_STYLE[f.status === 'present' ? 'good' : f.status] || COND_STYLE.unknown}">${this.esc(f.status)}</span> ${this.esc(f.item)}${f.note ? ` — <span style="color:var(--text-muted)">${this.esc(f.note)}</span>` : ''}</div>`).join('') : '<div class="fr" style="color:var(--text-muted)">No findings returned (set ANTHROPIC_API_KEY for AI analysis).</div>'}
          <div style="font-size:11px; color:var(--text-faint); margin-top:6px;">Conditions above were updated from these findings${this.keptByHand.length ? ` — except ${this.esc(this.keptByHand.join(', '))}, which you set by hand while the AI ran` : ''}${this.keptNewer.length ? `${this.keptByHand.length ? '; and' : ' — except'} ${this.esc(this.keptNewer.join(', '))}, which a newer deep inspection already graded` : ''}.</div>
        </div>` : this.renderDiState(di)}`;
  }

  // AI-unblock: the latest deep inspection's AI state — pending, unavailable
  // (out of credits / slow / no key), or the usual last-run summary.
  private renderDiState(di: any): string {
    if (!di) return '';
    const photos = (di.photo_urls || []).length;
    if (di.ai_status === 'pending') {
      return `<div class="ffe-find" style="background:#fff;"><h4>⏳ AI findings pending…</h4><div class="fr" style="color:var(--text-muted)">${photos} photo${photos === 1 ? '' : 's'} saved. Set conditions by hand meanwhile — anything you set won't be overwritten when the AI finishes.</div></div>`;
    }
    if (di.ai_status === 'failed' || di.ai_status === 'skipped') {
      return `<div class="ffe-find" style="background:#fff;"><h4>AI findings unavailable</h4><div class="fr" style="color:var(--text-muted)">${photos} photo${photos === 1 ? '' : 's'} saved${di.ai_label ? ` · ${this.esc(di.ai_label)}` : ''}. Set conditions by hand from the photos.</div></div>`;
    }
    return `<div class="ffe-find" style="background:#fff;"><h4>Last deep inspection · ${this.esc(String(di.at).slice(0, 10))}</h4><div class="fr" style="color:var(--text-muted)">${di.worn} worn · ${di.missing} missing · ${photos} photos</div></div>`;
  }

  private bind(): void {
    const sel = this.container.querySelector<HTMLSelectElement>('#ffe-prop');
    sel?.addEventListener('change', () => { this.selectedId = sel.value; this.lastFindings = null; this.pendingDi = ''; this.note = ''; this.loadProperty(); });

    this.container.querySelectorAll<HTMLSelectElement>('[data-cond-item]').forEach(s => s.addEventListener('change', async () => {
      const id = s.dataset.condItem!;
      try {
        await fetch(`${API}/ffe/${encodeURIComponent(this.selectedId)}/item/${encodeURIComponent(id)}/condition`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ condition: s.value }),
        });
        await this.reloadBoard(); await this.loadProperty();
      } catch (_) { /* */ }
    }));

    this.container.querySelectorAll<HTMLInputElement>('[data-photo-item]').forEach(inp => inp.addEventListener('change', async () => {
      const f = inp.files?.[0]; if (!f) return;
      const fd = new FormData(); fd.append('photo', f);
      this.note = 'Uploading photo…'; this.draw();
      try {
        const r = await fetch(`${API}/ffe/${encodeURIComponent(this.selectedId)}/item/${encodeURIComponent(inp.dataset.photoItem!)}/photo`, { method: 'POST', body: fd });
        if (!r.ok) throw new Error('upload failed');
        this.note = ''; await this.loadProperty();
      } catch (e: any) { this.note = `Photo failed: ${e.message}`; this.draw(); }
    }));

    const deep = this.container.querySelector<HTMLInputElement>('#ffe-deep');
    deep?.addEventListener('change', async () => {
      const files = Array.from(deep.files || []); if (!files.length) return;
      this.busy = true; this.note = ''; this.lastFindings = null; this.keptByHand = []; this.keptNewer = []; this.draw();
      try {
        const fd = new FormData(); for (const f of files) fd.append('photo', f, f.name);
        const r = await fetch(`${API}/ffe/${encodeURIComponent(this.selectedId)}/deep-inspect`, { method: 'POST', body: fd });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || 'inspection failed');
        this.busy = false;
        // AI-unblock: the photos are saved; the AI findings arrive in the background.
        const di = j.deep_inspection || {};
        if (di.ai_status === 'done') this.lastFindings = di.findings || [];
        await this.reloadBoard(); await this.loadProperty();   // starts the pending-findings watch
      } catch (e: any) { this.busy = false; this.note = `Deep inspection failed: ${e.message}`; this.draw(); }
    });

    this.container.querySelectorAll<HTMLElement>('[data-photo]').forEach(img => img.addEventListener('click', () => openExternal((img as HTMLImageElement).dataset.photo!, '_blank')));

    // Edit (rename / change expected count) an existing item.
    this.container.querySelectorAll<HTMLElement>('[data-edit-item]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.editItem!;
      const it = (this.data?.items || []).find((x: any) => x.id === id);
      if (!it) return;
      const name = prompt('Item name:', it.name);
      if (name == null || !name.trim()) return;
      const qtyRaw = prompt('Expected count:', String(it.expected_qty ?? 1));
      const expected_qty = qtyRaw == null ? it.expected_qty : Math.max(1, Number(qtyRaw) || 1);
      await this.saveItem({ id, name: name.trim(), category: it.category, expected_qty });
    }));

    // Remove an item from the kit.
    this.container.querySelectorAll<HTMLElement>('[data-del-item]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.delItem!;
      const it = (this.data?.items || []).find((x: any) => x.id === id);
      if (!confirm(`Remove "${it?.name || 'this item'}" from this property's kit?`)) return;
      try {
        await fetch(`${API}/ffe/${encodeURIComponent(this.selectedId)}/item/${encodeURIComponent(id)}`, { method: 'DELETE' });
        await this.reloadBoard(); await this.loadProperty();
      } catch (_) { /* */ }
    }));

    // Add a new item to the kit.
    this.container.querySelector<HTMLButtonElement>('#ffe-add-btn')?.addEventListener('click', async () => {
      const name = (this.container.querySelector('#ffe-add-name') as HTMLInputElement)?.value.trim();
      if (!name) { (this.container.querySelector('#ffe-add-name') as HTMLInputElement)?.focus(); return; }
      const category = (this.container.querySelector('#ffe-add-cat') as HTMLSelectElement)?.value || 'General';
      const expected_qty = Math.max(1, Number((this.container.querySelector('#ffe-add-qty') as HTMLInputElement)?.value) || 1);
      await this.saveItem({ name, category, expected_qty });
    });
  }

  // AI-unblock: check back every 8 s (up to ~6 min — past the server's AI
  // timeout) until the deep inspection's findings land or it's marked failed;
  // then refresh once. Holds the redraw while someone is typing on the page.
  private watchDeepInspection(listingId: string, diId: string): void {
    this.pendingDi = diId;
    const started = Date.now();
    const tick = async () => {
      if (this.pendingDi !== diId || this.selectedId !== listingId) return;   // moved on
      const d = await fetch(`${API}/ffe/${encodeURIComponent(listingId)}`).then(r => r.ok ? r.json() : null).catch(() => null);
      if (this.pendingDi !== diId || this.selectedId !== listingId) return;
      const di = d ? (d.deep_inspections || []).find((x: any) => x && x.id === diId) : null;
      const typing = !!document.activeElement && this.container.contains(document.activeElement) && /^(INPUT|TEXTAREA)$/.test(document.activeElement.tagName);
      if (di && di.ai_status !== 'pending' && !typing) {
        this.pendingDi = '';
        this.data = d;
        if (di.ai_status === 'done') { this.lastFindings = di.findings || []; this.keptByHand = di.kept_by_hand || []; this.keptNewer = di.kept_newer || []; }
        await this.reloadBoard();
        this.draw();
        return;
      }
      if (Date.now() - started > 360000) { this.pendingDi = ''; return; }
      setTimeout(() => { void tick(); }, 8000);
    };
    setTimeout(() => { void tick(); }, 8000);
  }

  private async saveItem(body: any): Promise<void> {
    this.note = 'Saving…'; this.draw();
    try {
      const r = await fetch(`${API}/ffe/${encodeURIComponent(this.selectedId)}/item`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('save failed');
      this.note = ''; await this.reloadBoard(); await this.loadProperty();
    } catch (e: any) { this.note = `Save failed: ${e.message}`; this.draw(); }
  }
}
